import { AnimatePresence, motion } from "framer-motion";
import { Plus, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { NavLink } from "react-router";
import { createDiagnosticsPattern, getDiagnosticPatterns } from "./api";
import BadgeBrainRegion from "./components/ui/BadgeBrainRegion";
import BaseModal from "./components/ui/BaseModal";
import BrainRegionListbox from "./components/ui/BrainRegionListbox";
import Button from "./components/ui/Button";
import Card from "./components/ui/Card";
import Input from "./components/ui/Input";
import Textarea from "./components/ui/Textarea";
import { useDebounce } from "./hooks/useDebounce";
import { getUniqueBrainAreasFromChannels } from "./lib/utils";
import BaseTemplate from "./templates/BaseTemplate";
import Pagination from "./components/ui/Pagination";

const DiagnosticsCenter = () => {
  const [patterns, setPatterns] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const searchQuery = useDebounce(searchInput, 1000);
  const [brainRegion, setBrainRegion] = useState([]);
  const brainRegionQuery = useDebounce(brainRegion, 1000);
  const [showModal, setShowModal] = useState(false);
  const [newPattern, setNewPattern] = useState({
    pattern_signature: "",
    description: "",
    involved_channels: "",
    involved_bands: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  const fetchPatterns = async (searchQuery, brainRegionQuery, page) => {
    try {
      const response = await getDiagnosticPatterns({
        search: searchQuery ?? "",
        brain_region:
          (brainRegionQuery && brainRegionQuery.map((v) => v.toLowerCase())) ??
          "",
        page: page,
      });
      setPatterns(response.data || []);
      setTotalPages(response.data.total_pages);
    } catch (err) {
      console.error("Error fetching patients:", err);
      // setError(err.response?.data?.detail || "Failed to fetch patients");
      setPatterns([]);
    }
  };

  useEffect(() => {
    fetchPatterns();
  }, []);

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
  };

  useEffect(() => {
    fetchPatterns(searchQuery, brainRegionQuery, page);
  }, [searchQuery, brainRegionQuery, page]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewPattern((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      const patternToCreate = {
        ...newPattern,
        involved_channels: newPattern.involved_channels.split(","),
        involved_bands: newPattern.involved_bands.split(","),
      };

      await createDiagnosticsPattern(patternToCreate);
      setNewPattern({
        pattern_signature: "",
        description: "",
        involved_channels: "",
        involved_bands: "",
      });
    } catch (err) {
      console.error("Error creating pattern:", err);
      setError(err.response?.data?.detail || err.message);
    }
    setIsLoading(false);
  };

  const handleChangePage = (_page) => {
    setPage(_page);
  };

  return (
    <BaseTemplate>
      <Card name={"Diagnostics Patterns"} className={""}>
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-2">
            <h3 className="text-lg text-[#1c3f70] font-semibold">Filters</h3>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Input
                  type="text"
                  placeholder="Search patterns..."
                  value={searchInput}
                  onChange={handleSearchChange}
                  className="pl-10 text-sm"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
              </div>
              <BrainRegionListbox
                value={brainRegion}
                onChange={setBrainRegion}
              />
              {/* Min Occurences:
              <div className="flex">
                <Input
                  type="number"
                  placeholder="0"
                  min="0"
                  value={minOccurencesFilter}
                  onChange={handleMinOccurencesFilterChange}
                  className="text-sm w-20"
                />
              </div> */}
              <Button
                onClick={() => setShowModal(true)}
                className="text-white px-3 py-1 md:px-4 md:py-1.5 ml-auto"
              >
                <Plus />
                Add Pattern
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  {[
                    "Pattern Signature",
                    "Description",
                    "Suggested",
                    "Confirmed",
                    "Last Seen",
                    "ITs",
                    "Regions",
                  ].map((name) => (
                    <th
                      key={name}
                      className="p-2 text-left text-base font-medium"
                    >
                      {name === "ITs" ? (
                        <abbr title="Interpretation Themes">{name}</abbr>
                      ) : (
                        name
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {patterns?.items?.map((pattern, index) => (
                  <tr
                    key={pattern.id}
                    className="border-t border-b border-gray-100 even:bg-gray-100 transition-colors"
                  >
                    <td className="p-2 underline decoration-blue-500">
                      <NavLink
                        to={`/diagnostics-center/patterns/${pattern.id}`}
                      >
                        {pattern.pattern_signature}
                      </NavLink>
                    </td>
                    <td className="p-2">
                      <span className="line-clamp-3">
                        {pattern.description}
                      </span>
                    </td>
                    <td className="p-2">{pattern.suggested_count}</td>
                    <td className="p-2">{pattern.confirmed_count}</td>
                    <td className="p-2">
                      {pattern.last_detected_at
                        ? new Date(pattern.last_detected_at).toLocaleString()
                        : "N/A"}
                    </td>
                    <td className="p-2">{pattern.themes_count}</td>
                    <td className="p-2">
                      <div className="flex flex-row flex-wrap gap-2">
                        {getUniqueBrainAreasFromChannels(
                          pattern.involved_channels ?? []
                        ).map((brainArea) => (
                          <BadgeBrainRegion
                            key={`${index}-${brainArea}`}
                            region={brainArea}
                          />
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            page={page}
            totalPages={totalPages}
            onChangePage={handleChangePage}
            className="ml-auto"
          />
        </div>
        <BaseModal
          open={showModal}
          onClose={() => setShowModal(false)}
          title="Add Pattern"
        >
          <AnimatePresence>
            <form onSubmit={handleSubmit} className="space-y-3 md:space-y-4">
              <Input
                label="Pattern Signature"
                type="text"
                onChange={handleInputChange}
                name="pattern_signature"
                value={newPattern.pattern_signature}
                placeholder="Pattern name"
                required
                className="mt-3 w-full"
              />
              <Textarea
                label="Description"
                onChange={handleInputChange}
                name="description"
                value={newPattern.description}
                placeholder="Comprehensive explanation of the pattern"
                rows={2}
                className="text-sm"
              />
              <Input
                label="Involved Channels"
                type="text"
                onChange={handleInputChange}
                name="involved_channels"
                value={newPattern.involved_channels}
                placeholder="F3,F4"
                required
                className="mt-3 w-full"
              />
              <Input
                label="Involved Bands"
                type="text"
                onChange={handleInputChange}
                name="involved_bands"
                value={newPattern.involved_bands}
                placeholder="delta,theta"
                required
                className="mt-3 w-full"
              />
              <div className="flex space-x-2 pt-1 md:pt-2">
                <Button
                  type="submit"
                  disabled={isLoading}
                  className={`px-3 py-1.5 md:px-5 md:py-2 bg-gradient-to-r from-[#2a5caa] to-[#3963aa] text-white rounded-lg text-xs md:text-sm font-medium shadow-sm hover:shadow-md transition-all ${
                    isLoading ? "opacity-70 cursor-not-allowed" : ""
                  }`}
                >
                  {isLoading ? "Creating..." : "Create Pattern"}
                </Button>
              </div>
              {error && (
                <motion.div
                  className="p-2 md:p-3 bg-red-50 border border-red-200 rounded-lg"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <p className="text-red-600 text-xs md:text-sm">
                    {JSON.stringify(error)}
                  </p>
                </motion.div>
              )}
            </form>
          </AnimatePresence>
        </BaseModal>
      </Card>
    </BaseTemplate>
  );
};

export default DiagnosticsCenter;
